import { FunctionComponent } from 'react';
import { Route } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Configuration } from '../../../shared/models/configuration';
import {
	InventoryAllocationReport,
	InventoryAllocationReportStatus,
} from '../../../shared/models/inventoryAllocationReport';
import InventoryAllocationReportConfiguration from './InventoryAllocationReportConfiguration';
import InventoryAllocationReportResults from './InventoryAllocationReportResults';
import Tabs, { TabLink } from '../components/Tabs';
import { useReports } from '../../../domains/domains';
import { GET_INVENTORY_ALLOCATION_REPORT } from '../../../shared/api/inventory-allocation-reports';
import InventoryAllocationReportInProgress from './InventoryAllocationReportInProgress';
import { InventoryAllocation } from '../../../shared/models/inventoryAllocation';
import InventoryAllocationReportNoMovements from './InventoryAllocationReportNoMovements';
import InventoryAllocationReportMovements from './InventoryAllocationReportMovements';
import ProductsTable from '../components/ProductsTable';
import LocationsTable from '../components/LocationsTable';
import { ScopeTypeDTO } from '../../../shared/models/schema';

interface Props {
	inventoryAllocationId: InventoryAllocation['id'];
	id: InventoryAllocationReport['id'];
	rootURL: string;
	isCopyingConfiguration?: boolean;
	isAddingDraft?: boolean;
	onCopyConfiguration: (configuration: Configuration) => void;
	onAddDraft: () => void;
}

const InventoryAllocationReportDetail: FunctionComponent<Props> = ({
	inventoryAllocationId,
	id,
	rootURL,
	isCopyingConfiguration,
	isAddingDraft,
	onCopyConfiguration,
	onAddDraft,
}) => {
	const result = useQuery(['reports', id], () =>
		GET_INVENTORY_ALLOCATION_REPORT(inventoryAllocationId, id)
	);
	const { entity: report, isLoading } = useReports(result);

	const tabs: TabLink[] = [
		{
			id: 'results',
			label: 'Results',
			to: `${rootURL}/results`,
		},
		{
			id: 'movements',
			label: 'Movements',
			to: `${rootURL}/movements`,
		},
		{
			id: 'configuration',
			label: 'Configuration',
			to: `${rootURL}/configuration`,
		},
		{
			id: 'scope',
			label: 'Scope',
			to: `${rootURL}/scope`,
		},
	];

	if (isLoading) {
		return <p>Loading...</p>;
	}

	if (!report) {
		return <p>Report not found</p>;
	}

	return (
		<>
			<div className="my-6">
				<Tabs tabs={tabs} />
			</div>
			<Route
				path={tabs[0].to}
				render={() => {
					if (report.status === InventoryAllocationReportStatus.InProgress) {
						return (
							<InventoryAllocationReportInProgress
								addDraft={onAddDraft}
								isAddingDraft={isAddingDraft}
							/>
						);
					}

					if (
						report.status === InventoryAllocationReportStatus.NoSuggestedMoves
					) {
						return (
							<InventoryAllocationReportNoMovements
								addDraft={onAddDraft}
								isAddingDraft={isAddingDraft}
							/>
						);
					}

					return (
						<InventoryAllocationReportResults
							reportId={report.id}
							allocationId={inventoryAllocationId}
						/>
					);
				}}
			/>
			<Route
				path={tabs[1].to}
				render={() => {
					if (report.status === InventoryAllocationReportStatus.InProgress) {
						return (
							<InventoryAllocationReportInProgress
								addDraft={onAddDraft}
								isAddingDraft={isAddingDraft}
							/>
						);
					}

					if (
						report.status === InventoryAllocationReportStatus.NoSuggestedMoves
					) {
						return (
							<InventoryAllocationReportNoMovements
								addDraft={onAddDraft}
								isAddingDraft={isAddingDraft}
							/>
						);
					}

					return (
						<InventoryAllocationReportMovements
							reportId={report.id}
							allocationId={inventoryAllocationId}
						/>
					);
				}}
			/>
			<Route
				path={tabs[2].to}
				render={() => {
					return (
						<InventoryAllocationReportConfiguration
							inventoryAllocationId={inventoryAllocationId}
							defaultFilters={
								(
									result.data?.scopes?.find(
										({ scope_type }) => scope_type === ScopeTypeDTO.ProductScope
									) as any
								)?.filters
							}
							reportId={id}
							{...report.configuration}
							isInProgress={
								report.status === InventoryAllocationReportStatus.InProgress
							}
							isProposal={
								report.status === InventoryAllocationReportStatus.Proposal
							}
							onCopyConfiguration={() =>
								onCopyConfiguration(report.configuration)
							}
							isCopyingConfiguration={isCopyingConfiguration}
						/>
					);
				}}
			/>
			<Route
				path={tabs[3].to}
				render={() => {
					return (
						<>
							<ProductsTable
								isReadOnly
								inventoryAllocationId={inventoryAllocationId}
								reportId={id}
								defaultFilters={
									(
										result.data?.scopes?.find(
											({ scope_type }) =>
												scope_type === ScopeTypeDTO.ProductScope
										) as any
									)?.filters
								}
								selectedProducts={report.configuration.scope.products}
							/>
							<LocationsTable
								inventoryAllocationId={inventoryAllocationId}
								reportId={id}
								isReadOnly
								className="mt-12"
								selectedLocations={report.configuration.scope.locations}
							/>
						</>
					);
				}}
			/>
		</>
	);
};

export default InventoryAllocationReportDetail;
